/**
 * High-Vol Regime Dashboard
 * 3-layer risk detection: Core Signals, Market Breadth, Derivatives
 */
import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, CircularProgress, Skeleton, alpha } from '@mui/material';
import {
  ShowChart as ShowChartIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { highVolRegimeAPI } from '../services/api';
import HighVolRegimeCard from '../components/high-vol/HighVolRegimeCard';
import RegimeTimelineChart from '../components/high-vol/RegimeTimelineChart';
import TriggerBreakdownChart from '../components/high-vol/TriggerBreakdownChart';
import ScoreComponentsChart from '../components/high-vol/ScoreComponentsChart';

// ── Risk config ──
const RISK_CFG = {
  LOW:    { color: '#10b981', Icon: CheckCircleIcon, key: 'low' },
  MEDIUM: { color: '#f59e0b', Icon: WarningIcon,     key: 'medium' },
  HIGH:   { color: '#ef4444', Icon: ErrorIcon,        key: 'high' },
};

// ── Stat Tile ──
const Tile = ({ label, value, sub, color, Icon }) => (
  <Box sx={{
    position: 'relative', overflow: 'hidden',
    borderRadius: 1.5, border: `1px solid ${alpha(color, 0.15)}`,
    bgcolor: '#fff', p: '14px 16px', transition: 'all 0.2s',
    '&:hover': { borderColor: alpha(color, 0.3), boxShadow: `0 4px 16px ${alpha(color, 0.08)}`, transform: 'translateY(-1px)' },
  }}>
    <Box sx={{ position: 'absolute', top: 0, right: 0, width: 64, height: 64, background: `radial-gradient(circle at top right, ${alpha(color, 0.06)} 0%, transparent 70%)` }} />
    <Box sx={{ position: 'relative', display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
      <Box sx={{ width: 38, height: 38, borderRadius: 1.5, flexShrink: 0, background: `linear-gradient(135deg, ${color} 0%, ${alpha(color, 0.7)} 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 3px 8px ${alpha(color, 0.25)}` }}>
        <Icon sx={{ fontSize: 20, color: '#fff' }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#94a3b8', lineHeight: 1, mb: 0.5 }}>{label}</Typography>
        <Typography sx={{ fontSize: '1.75rem', fontWeight: 900, color, lineHeight: 1.2, fontFamily: 'monospace' }}>{value}</Typography>
        {sub && <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8', mt: 0.4 }}>{sub}</Typography>}
      </Box>
    </Box>
  </Box>
);

// ── Skeleton tiles ──
const TileSkeleton = () => (
  <Box sx={{ borderRadius: 1.5, border: '1px solid #e2e8f0', bgcolor: '#fff', p: '14px 16px' }}>
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
      <Skeleton variant="rounded" width={38} height={38} sx={{ borderRadius: 1.5 }} />
      <Box sx={{ flex: 1 }}>
        <Skeleton width={70} height={12} sx={{ mb: 0.8 }} />
        <Skeleton width={50} height={26} />
      </Box>
    </Box>
  </Box>
);

// ── Index Card Skeleton ──
const IndexCardSkeleton = () => (
  <Box sx={{ borderRadius: 1.5, border: '1px solid #e2e8f0', bgcolor: '#fff', overflow: 'hidden' }}>
    {/* Top color bar */}
    <Skeleton variant="rectangular" height={3} sx={{ width: '100%' }} />
    
    <Box sx={{ p: 1.5 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.2, pb: 1, borderBottom: '1px solid #f1f5f9' }}>
        <Skeleton width={80} height={24} />
        <Skeleton variant="rounded" width={100} height={24} sx={{ borderRadius: 1.5 }} />
      </Box>
      
      {/* Score + Action */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 1.2, pb: 1, borderBottom: '1px solid #f1f5f9' }}>
        <Box sx={{ flex: 1 }}>
          <Skeleton width={70} height={12} sx={{ mb: 0.3 }} />
          <Skeleton width={60} height={28} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Skeleton width={60} height={12} sx={{ mb: 0.3 }} />
          <Skeleton width={80} height={20} />
        </Box>
      </Box>
      
      {/* Metrics grid */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.8, mb: 1.2, pb: 1, borderBottom: '1px solid #f1f5f9' }}>
        {[1,2,3,4].map(i => (
          <Box key={i}>
            <Skeleton width={60} height={10} sx={{ mb: 0.2 }} />
            <Skeleton width={40} height={18} />
          </Box>
        ))}
      </Box>
      
      {/* Signal pills */}
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
        {[1,2,3].map(i => (
          <Skeleton key={i} variant="rounded" width={50 + i * 10} height={20} sx={{ borderRadius: 1 }} />
        ))}
      </Box>
    </Box>
  </Box>
);

// ══════════════════════════════════════
function HighVolDashboard() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summaryData, setSummaryData] = useState(null);
  const [historyData, setHistoryData] = useState({});
  const [calculating, setCalculating] = useState(false);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []); // eslint-disable-line

  const handleRecalculate = async () => {
    try {
      setCalculating(true);
      await highVolRegimeAPI.recalculate();
      await new Promise(r => setTimeout(r, 10000));
      await fetchData();
    } catch (err) {
      setError(t('highVolRegime.errors.recalculateFailed', 'Failed to recalculate'));
    } finally { setCalculating(false); }
  };

  const fetchData = async () => {
    try {
      setLoading(true); setError(null);
      const summaryRes = await highVolRegimeAPI.getSummary();
      setSummaryData(summaryRes.data);

      const indexes = ['VNINDEX', 'VN30', 'VN100'];
      const histResults = await Promise.all(
        indexes.map(idx =>
          highVolRegimeAPI.getHistory(idx, 30)
            .then(res => ({ idx, data: res.data }))
            .catch(() => ({ idx, data: [] }))
        )
      );
      const hMap = {};
      histResults.forEach(({ idx, data }) => { hMap[idx] = data; });
      setHistoryData(hMap);
    } catch (err) {
      setError(err.message || t('highVolRegime.errors.loadFailed', 'Failed to load data'));
    } finally { setLoading(false); }
  };

  const risk = RISK_CFG[summaryData?.overall_risk_level] || RISK_CFG.LOW;
  const breadthPct = summaryData?.market_breadth_avg != null ? (summaryData.market_breadth_avg * 100).toFixed(1) : null;
  const hvCount = summaryData?.high_vol_count || 0;

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', px: { xs: 1.5, md: 3 }, py: 2 }}>
      {/* ── Header ─────────────────────────────────────── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 44, height: 44, borderRadius: '12px', flexShrink: 0,
            background: 'linear-gradient(135deg, #ef4444 0%, #f59e0b 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 3px 10px rgba(239, 68, 68, 0.25)',
          }}>
            <ShowChartIcon sx={{ fontSize: 22, color: '#fff' }} />
          </Box>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontWeight: 700, color: '#0f172a', fontSize: '1.2rem', letterSpacing: '-0.01em' }}>
                {t('highVolRegime.title', 'High-Vol Regime')}
              </Typography>
              {!loading && summaryData && (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, bgcolor: '#f1f5f9', px: 1, py: 0.3, borderRadius: 1.5 }}>
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#ef4444', boxShadow: '0 0 6px #ef4444' }} />
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#475569' }}>
                      {summaryData.trade_date}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: '#f1f5f9', px: 1, py: 0.3, borderRadius: 1.5 }}>
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: risk.color, boxShadow: `0 0 6px ${risk.color}` }} />
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#475569' }}>
                      {summaryData.overall_risk_level}
                    </Typography>
                  </Box>
                </>
              )}
            </Box>
            <Typography sx={{ color: '#94a3b8', fontSize: '0.75rem', mt: 0.15 }}>
              {t('highVolRegime.subtitle', 'Phát hiện rủi ro 3 lớp: Core, Breadth, Derivatives')}
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={calculating ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon sx={{ fontSize: 16 }} />}
          onClick={handleRecalculate} disabled={calculating || loading}
          sx={{
            textTransform: 'none', fontSize: 13, fontWeight: 600, borderRadius: '4px',
            bgcolor: '#ef4444', color: '#fff', px: 2,
            '&:hover': { bgcolor: '#dc2626' },
            '&:disabled': { bgcolor: '#94a3b8', color: '#fff' },
          }}>
          {calculating ? t('common.calculating', 'Calculating...') : t('common.refresh', 'Refresh')}
        </Button>
      </Box>

      {/* Error */}
      {error && !summaryData && (
        <Box sx={{ px: 2, py: 1.5, mb: 2, borderRadius: 1.5, bgcolor: alpha('#ef4444', 0.06), border: `1px solid ${alpha('#ef4444', 0.15)}` }}>
          <Typography sx={{ fontSize: '0.8rem', color: '#ef4444' }}>{error}</Typography>
        </Box>
      )}

      {/* Stat tiles */}
      {loading && !summaryData ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.5, mb: 2 }}>
          {[0,1,2].map(i => <TileSkeleton key={i} />)}
        </Box>
      ) : summaryData && (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.5, mb: 2 }}>
          <Tile
            label={t('highVolRegime.overallRisk', 'Overall Risk')}
            value={t(`highVolRegime.riskLevels.${summaryData.overall_risk_level}`, summaryData.overall_risk_level)}
            color={risk.color} Icon={risk.Icon}
          />
          <Tile
            label={t('highVolRegime.breadthMetrics', 'Market Breadth')}
            value={breadthPct != null ? `${breadthPct}%` : 'N/A'}
            color={breadthPct && parseFloat(breadthPct) < 45 ? '#f59e0b' : '#3b82f6'}
            Icon={ShowChartIcon}
          />
          <Tile
            label={t('highVolRegime.highVolCount', 'HIGH_VOL Indexes')}
            value={`${hvCount} / 3`}
            color={hvCount >= 2 ? '#ef4444' : hvCount === 1 ? '#f59e0b' : '#10b981'}
            Icon={WarningIcon}
          />
        </Box>
      )}

      {/* HIGH risk banner */}
      {summaryData?.overall_risk_level === 'HIGH' && (
        <Box sx={{ px: 2, py: 1, mb: 2, borderRadius: 1.5, bgcolor: alpha('#ef4444', 0.06), border: `1px solid ${alpha('#ef4444', 0.15)}` }}>
          <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#ef4444' }}>
            {t('highVolRegime.highRiskBanner', { count: summaryData.high_vol_count, defaultValue: `HIGH RISK: ${summaryData.high_vol_count} index(es) in HIGH_VOL. Consider reducing exposure.` })}
          </Typography>
        </Box>
      )}

      {/* Index Cards */}
      <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#1e293b', mb: 1, mt: 0.5 }}>
        {t('highVolRegime.indexAnalysis', 'Index Analysis')}
      </Typography>
      {summaryData?.indexes ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.5, mb: 2 }}>
          {[...summaryData.indexes]
            .sort((a, b) => {
              const ord = { VNINDEX: 1, VN30: 2, VN100: 3 };
              return (ord[a.index_code] || 99) - (ord[b.index_code] || 99);
            })
            .map(d => <HighVolRegimeCard key={d.index_code} data={d} />)}
        </Box>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.5, mb: 2 }}>
          {[0,1,2].map(i => <IndexCardSkeleton key={i} />)}
        </Box>
      )}

      {/* Charts Section */}
      <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#1e293b', mb: 1, mt: 1 }}>
        {t('highVolRegime.history', 'Historical Analysis')}
      </Typography>

      {/* Timeline Charts */}
      {loading && !historyData['VNINDEX'] ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
          {[1,2,3].map(i => (
            <Box key={i} sx={{ bgcolor: '#fff', border: '1px solid #e2e8f0', borderRadius: 1.5, p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Skeleton width={100} height={20} />
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  {[1,2,3].map(j => <Skeleton key={j} variant="rounded" width={70} height={22} sx={{ borderRadius: 1 }} />)}
                </Box>
              </Box>
              <Skeleton variant="rounded" height={180} sx={{ borderRadius: 1.5 }} />
            </Box>
          ))}
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
          {['VNINDEX', 'VN30', 'VN100'].map(idx =>
            historyData[idx] && historyData[idx].length > 0 && (
              <RegimeTimelineChart key={`tl-${idx}`} data={historyData[idx]} indexCode={idx} />
            )
          )}
        </Box>
      )}

      {/* Score Components + Trigger Breakdown side by side */}
      {loading && !historyData['VN30'] ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
          {[1,2].map(i => (
            <Box key={i} sx={{ bgcolor: '#fff', border: '1px solid #e2e8f0', borderRadius: 1.5, p: 2 }}>
              <Skeleton width={120} height={20} sx={{ mb: 1.5 }} />
              <Skeleton variant="rounded" height={280} sx={{ borderRadius: 1.5 }} />
            </Box>
          ))}
        </Box>
      ) : historyData['VN30'] && historyData['VN30'].length > 0 && (
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
          <ScoreComponentsChart data={historyData['VN30']} indexCode="VN30" />
          <TriggerBreakdownChart data={historyData['VN30']} indexCode="VN30" />
        </Box>
      )}
    </Box>
  );
}

export default HighVolDashboard;
